import React from 'react';
import PropTypes from 'prop-types';
import {
  getRoutePathById,
  withStandaloneSiteMap,
  withToolsSiteMap,
  withImportSiteMap,
  withHotelCenterSiteMap,
  withHotelCenterV2SiteMap,
  withAdInsightSiteMap,
  withBidStrategiesSiteMap,
  withSiteMap,
  processor,
} from '@bingads-webui-campaign-react/with-site-map';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { ElementSelector } from '@bingads-webui-campaign-react/element-selector';
import { withLastURL } from '@bingads-webui-campaign-react/with-last-url';
import { withScope } from '@bingads-webui-react/scope-service';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';
import { SummaryBarPlaceholder } from '@bingads-webui-campaign-react/shell-ui-common';
import { CampaignScopeBarLoader } from '@bingads-webui-campaign-react/scope-bar';
import { CampaignSummaryBarLoader } from './campaign-summary-bar-loader';
import { SharedBudgetSummaryBarLoader } from './shared-budget-summary-bar-loader';
import { AccountsSummaryBarLoader } from './accounts-summary-bar-loader';
import { SimpleSummaryBarLoader } from './simple-summary-bar-loader';
import { GeneralSummaryBarLoader } from './general-summary-bar-loader';
import { SiteExclusionListLinkedPageLoader } from './site-exclusion-list-linked-page-loader';
import { NegativeKeywordsListDetailsLoader } from './negative-keywords-list-details-summary-bar-loader';
import { PortfolioDetailsLoader } from './portfolio-details-summary-bar-loader';
import { MerchantCenterSummaryBarLoader } from './merchant-center-summary-bar-loader';
import { QueryInsightsSummaryBarLoader } from './query-insights-summary-bar-loader';
import { UetDashboardLoader } from './uet-dashboard-summary-bar-loader';
import { AssetLibrarySummaryBarLoader as AssetLibrarySummaryBarLoaderInternal } from './asset-library-summary-bar-loader';
import { AdStudioSummaryBarLoader as AdStudioSummaryBarLoaderInternal } from './ad-studio-summary-bar-loader';
import { PmaxAssetDetailsSummaryBarLoader } from './pmax-asset-details-summary-bar-loader';
import { BudgetCenterSummaryBarLoader as BudgetCenterSummaryBarLoaderInternal } from './budget-center-summary-bar-loader';
import { HotelCenterV2SummaryBarLoader as HotelCenterV2SummaryBarLoaderInternal } from './hotel-center-v2-summary-bar-loader';
import { AdEditorSummaryBarLoader as AdEditorSummaryBarLoaderInternal } from './ad-editor-summary-bar-loader';

import {
  getBulkDetailConfig,
  getRuleDetailConfig,
  getScriptsDetailConfig,
  getBulkUploadsBuildSpreadsheetConfig,
} from './general-summary-bar-configs/index';
import { BrandListsDetailsPageLoader } from './brand-lists-details-page-loader';

const summaryBarProcessors = [processor.processPermissions, processor.processPath];

const RuleDetailSummaryBarLoader = withLastURL(withStandaloneSiteMap(React.memo(GeneralSummaryBarLoader), summaryBarProcessors, 'rule-detail'));
const BulkDetailSummaryBarLoader = withLastURL(withStandaloneSiteMap(React.memo(GeneralSummaryBarLoader), summaryBarProcessors, 'bulk-edits-detail'));
const ScriptsDetailSummaryBarLoader = withLastURL(withStandaloneSiteMap(React.memo(GeneralSummaryBarLoader), summaryBarProcessors, 'scripts-detail'));
const BulkUploadsBuildSpreadsheetSummaryBarLoader = withLastURL(withStandaloneSiteMap(React.memo(GeneralSummaryBarLoader), summaryBarProcessors, 'bulk-uploads-build-spreadsheet'));
const NegativeKeywordsListDetailsSummaryBarLoader = withStandaloneSiteMap(React.memo(NegativeKeywordsListDetailsLoader), summaryBarProcessors, 'negative-keywords-lists-detail');
const PortfolioDetailsSummaryBarLoader = withStandaloneSiteMap(React.memo(PortfolioDetailsLoader), summaryBarProcessors, 'portfolio-bid-strategy-details');
const CampaignSummaryBarWithLastUrl = withLastURL(CampaignSummaryBarLoader);
const QueryInsightsSummaryBarWithLastUrl = withLastURL(QueryInsightsSummaryBarLoader);
const UetDashboardSummaryBarWithLastUrl = withLastURL(withToolsSiteMap(UetDashboardLoader, summaryBarProcessors, 'uet-tag'));
const SharedBudgetSummaryBarWithLastUrl = withLastURL(SharedBudgetSummaryBarLoader);
const StandaloneSummaryBarLoader = withLastURL(withStandaloneSiteMap(SimpleSummaryBarLoader, summaryBarProcessors));
const SiteExclusionListLinkedPageSummaryBarLoader = withLastURL(withToolsSiteMap(SiteExclusionListLinkedPageLoader, summaryBarProcessors));
const ToolsPageSummaryBarLoader = withLastURL(withToolsSiteMap(SimpleSummaryBarLoader, summaryBarProcessors));
const AudiencePlannerSummaryBarLoader = withLastURL(withToolsSiteMap(SimpleSummaryBarLoader, summaryBarProcessors, 'audience-planner'));
const FeedItemsPageSummaryBarLoader = withStandaloneSiteMap(
  SimpleSummaryBarLoader,
  summaryBarProcessors,
  ['ad-customizer-feeds-items', 'dynamic-data-feeds-items', 'page-feed-item']
);
const AdEditorSummaryBarLoader = withStandaloneSiteMap(
  AdEditorSummaryBarLoaderInternal,
  summaryBarProcessors,
  ['ad-editor']
);
const PerformanceTargetsPageSummaryBarLoader = withStandaloneSiteMap(
  SimpleSummaryBarLoader,
  summaryBarProcessors,
  ['performance-targets-create', 'performance-targets-overview']
);
const StandaloneAccountsSummaryBarLoader = withStandaloneSiteMap(
  SimpleSummaryBarLoader,
  summaryBarProcessors,
  ['account-create', 'account-linking', 'accounts-unlinking', 'accounts-transfer', 'pending-transfer-request']
);
const ImportPageSummaryBarLoader = withLastURL(withImportSiteMap(SimpleSummaryBarLoader, summaryBarProcessors));
const HotelCenterSummaryBarLoader = withLastURL(withHotelCenterSiteMap(SimpleSummaryBarLoader, summaryBarProcessors));
const HotelCenterV2SummaryBarLoader = withLastURL(withHotelCenterV2SiteMap(HotelCenterV2SummaryBarLoaderInternal, summaryBarProcessors));
const AdInsightSummaryBarLoader = withLastURL(withAdInsightSiteMap(SimpleSummaryBarLoader, summaryBarProcessors));
const AssetLibrarySummaryBarLoader = withLastURL(withToolsSiteMap(AssetLibrarySummaryBarLoaderInternal, summaryBarProcessors, 'creative-studio-asset-library'));
const AdStudioSummaryBarLoader = withLastURL(withToolsSiteMap(AdStudioSummaryBarLoaderInternal, summaryBarProcessors, 'ad-studio-app-entry'));
const BidStrategiesSummaryBarLoader = withLastURL(withBidStrategiesSiteMap(SimpleSummaryBarLoader, summaryBarProcessors));
const AssetGroupCreateAndEditSummaryBarWithLastUrl = withLastURL(withStandaloneSiteMap(
  SimpleSummaryBarLoader,
  summaryBarProcessors,
  ['asset-group-create', 'asset-group-edit']
));
const BudgetCenterSummaryBarLoader = withToolsSiteMap(BudgetCenterSummaryBarLoaderInternal, summaryBarProcessors, 'budget-center');
const BrandListsDetailsPageSummaryBarLoader = withLastURL(withToolsSiteMap(BrandListsDetailsPageLoader, summaryBarProcessors, 'brand-lists'));

export const SummaryBarContainerInternal = React.memo((props) => {
  const isNavRefresh = isNavigationRefreshOn(props.permissions);
  const isNavigationRefreshBudgetCenterOn = isNavRefresh && props.permissions.dynamic.NavigationRefreshBudgetCenter;
  return (
    <ElementSelector fallback={(<SummaryBarPlaceholder />)}>
      <MerchantCenterSummaryBarLoader {...props} />
      <AccountsSummaryBarLoader {...props} />
      <AssetGroupCreateAndEditSummaryBarWithLastUrl
        {...props}
        additionalPrefixItems={[{
          id: 'asset-groups',
          text: props.i18n.getString(_TL_('Asset groups')),
          onClick: () => {
            props.scope.remove(['assetGroupId']);
            props.scope.save(getRoutePathById(props.siteMap, 'asset-groups'));
          },
        }]}
      />
      <PmaxAssetDetailsSummaryBarLoader {...props} />
      {
        isNavRefresh
          ? <CampaignScopeBarLoader {...props} />
          : <CampaignSummaryBarWithLastUrl {...props} />
      }
      <BidStrategiesSummaryBarLoader {...props} />
      <BulkDetailSummaryBarLoader
        {...props}
        {...getBulkDetailConfig(props)}
      />
      <RuleDetailSummaryBarLoader
        {...props}
        {...getRuleDetailConfig(props)}
      />
      <ScriptsDetailSummaryBarLoader
        {...props}
        {...getScriptsDetailConfig(props)}
      />
      <BulkUploadsBuildSpreadsheetSummaryBarLoader
        {...props}
        {...getBulkUploadsBuildSpreadsheetConfig(props)}
      />
      <ImportPageSummaryBarLoader
        allowBackToSmartCampaign
        {...props}
      />
      <NegativeKeywordsListDetailsSummaryBarLoader
        {...props}
      />
      <PortfolioDetailsSummaryBarLoader {...props} />
      <HotelCenterSummaryBarLoader
        breadCrumbText={props.i18n.getString(_TL_('Microsoft Hotel Center'))}
        customSummaryBarItemsId="custom-summary-bar-items"
        {...props}
      />
      <HotelCenterV2SummaryBarLoader
        {...props}
      />
      <QueryInsightsSummaryBarWithLastUrl {...props} />
      <SharedBudgetSummaryBarWithLastUrl {...props} />
      <FeedItemsPageSummaryBarLoader {...props} />
      <PerformanceTargetsPageSummaryBarLoader {...props} />
      <AssetLibrarySummaryBarLoader {...props} />
      <AdStudioSummaryBarLoader {...props} />
      <UetDashboardSummaryBarWithLastUrl {...props} />
      {!isNavigationRefreshBudgetCenterOn && <BudgetCenterSummaryBarLoader {...props} />}
      <BrandListsDetailsPageSummaryBarLoader
        breadCrumbText={props.i18n.getString(_TL_('Brand lists'))}
        {...props}
      />
      <SiteExclusionListLinkedPageSummaryBarLoader {...props} />
      <StandaloneAccountsSummaryBarLoader
        backButtonText={props.i18n.getString(_TL_('Back to accounts'))}
        backButtonUrl="/campaign/vnext/accounts"
      />
      <AdEditorSummaryBarLoader as AdEditorSummaryBarLoaderInternal {...props} />
      <AudiencePlannerSummaryBarLoader breadCrumbText={props.i18n.getString(_TL_('Audience planner'))} {...props} />
      <StandaloneSummaryBarLoader {...props} />
      <ToolsPageSummaryBarLoader {...props} />
      <AdInsightSummaryBarLoader {...props} />
    </ElementSelector>
  );
});

SummaryBarContainerInternal.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    remove: PropTypes.func.isRequired,
    save: PropTypes.func.isRequired,
  }).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      NavigationRefresh: PropTypes.bool,
      NavigationRefreshBudgetCenter: PropTypes.bool,
    }),
  }).isRequired,
};

export const SummaryBarContainer = withSiteMap(withScope(withI18nConsumer(withPermissions(SummaryBarContainerInternal))));
